import { FrontendProduct } from "@redotech/redo-model/agents/support-agents/frontend-product";
import { parseNumericIdFromShopifyId } from "@redotech/shopify-util/id";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

export interface ProductByIdFetcherOptions {
  includeOutOfStock?: boolean;
}

export type ProductByIdFetcher = (
  productIds: string[],
  signal?: AbortSignal,
  options?: ProductByIdFetcherOptions,
) => Promise<FrontendProduct[]>;

export type BestSellersProductFetcher = (
  limit: number,
  signal?: AbortSignal,
) => Promise<FrontendProduct[]>;

export type ProductSearchFetcher = (
  query: string,
  signal?: AbortSignal,
) => Promise<FrontendProduct[]>;

export const useBestSellers = (
  teamId: string | undefined,
  fetcher: BestSellersProductFetcher | undefined,
  limit = 5,
): {
  bestSellers: FrontendProduct[];
  isLoading: boolean;
  error: Error | null;
} => {
  const {
    data: bestSellers = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["bestSellers", teamId, limit],
    queryFn: async ({ signal }): Promise<FrontendProduct[]> => {
      if (!teamId || !fetcher) {
        return [];
      }

      return await fetcher(limit, signal);
    },
    enabled: !!teamId && !!fetcher,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

  return { bestSellers, isLoading, error: error as Error | null };
};

export const useProducts = (
  teamId: string | undefined,
  fetcher: ProductByIdFetcher | undefined,
  productIds: string[],
): {
  productsMap: Map<string, FrontendProduct>;
  isLoading: boolean;
  error: Error | null;
} => {
  const queryClient = useQueryClient();

  const uniqueProductIds = useMemo(
    () =>
      [
        ...new Set(productIds.map((id) => parseNumericIdFromShopifyId(id))),
      ].sort(),
    [productIds],
  );

  const results = useQueries({
    queries:
      uniqueProductIds.length === 0
        ? []
        : uniqueProductIds.map((productId) => ({
            queryKey: ["product", teamId, productId],
            queryFn: async (): Promise<FrontendProduct | null> => {
              const cached = queryClient.getQueryData<FrontendProduct>([
                "product",
                teamId,
                productId,
              ]);
              if (cached) {
                return cached;
              }

              return null;
            },
            enabled: !!teamId && !!fetcher,
            staleTime: 1000 * 60 * 10, // 10 minutes
            gcTime: 1000 * 60 * 30, // 30 minutes
          })),
    combine: (results) => {
      const products: FrontendProduct[] = [];
      for (const r of results) {
        if (r.data && r.data.externalId) {
          products.push(r.data);
        }
      }
      const isLoading = results.some((r) => r.isLoading);
      const error = (results.find((r) => r.error)?.error as Error) ?? null;

      return { products, isLoading, error };
    },
  });

  const productsMap = useMemo(() => {
    const map = new Map<string, FrontendProduct>();
    for (const product of results.products) {
      const normalizedId = parseNumericIdFromShopifyId(product.externalId);
      map.set(normalizedId, product);
    }
    return map;
  }, [results.products]);

  const uncachedIds = useMemo(() => {
    if (!teamId) return [];
    return uniqueProductIds.filter(
      (id) =>
        !queryClient.getQueryData<FrontendProduct>(["product", teamId, id]),
    );
  }, [uniqueProductIds, teamId, queryClient]);

  const { isLoading: isBatchLoading, error: batchError } = useQuery({
    queryKey: ["productsBatch", teamId, uncachedIds.join(",")],
    queryFn: async ({ signal }): Promise<void> => {
      if (!teamId || !fetcher || uncachedIds.length === 0) {
        return;
      }

      const fetchedProducts = await fetcher(uncachedIds, signal);

      for (const product of fetchedProducts) {
        if (product && product.externalId) {
          const normalizedId = parseNumericIdFromShopifyId(product.externalId);
          queryClient.setQueryData(["product", teamId, normalizedId], product);
        }
      }
    },
    enabled: !!teamId && !!fetcher && uncachedIds.length > 0,
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 0, // Don't cache the batch query itself
  });

  return {
    productsMap,
    isLoading: results.isLoading || isBatchLoading,
    error: results.error ?? (batchError as Error | null),
  };
};

export const useSearchProducts = (
  teamId: string | undefined,
  fetcher: ProductSearchFetcher | undefined,
  searchQuery: string,
): { products: FrontendProduct[]; isLoading: boolean; error: Error | null } => {
  const {
    data: products = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["searchProducts", teamId, searchQuery],
    queryFn: async ({ signal }): Promise<FrontendProduct[]> => {
      if (!teamId || !fetcher || !searchQuery) {
        return [];
      }

      return await fetcher(searchQuery, signal);
    },
    enabled: !!teamId && !!fetcher && !!searchQuery,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });

  return { products, isLoading, error: error as Error | null };
};
